import { SurveyDefinition, SurveyStepDefinition } from "../../../types/Survey";
import { darkGradientImage, gradientImage } from "../../../helpers/surveyLookAndFeelHelper";
import { darkFill, darkText } from "../../../helpers/surveyStyleKeys";
import { ORG_COLOR } from "../../../helpers/surveyStyleDefaults";

export const stepStyles = (survey: SurveyDefinition, step: SurveyStepDefinition) => ({ ...survey.styles, ...step.styles });

export function getProgressIndicatorType(survey: SurveyDefinition, step: SurveyStepDefinition) {
    if (survey.steps.length == 1 || step.excludeFromProgressCalculation) {
        return 'None';
    }

    //default behavior
    if (!survey.progressIndicatorType) {
        if (survey.type === 'RKStudioNavigableOrderedTask') {
            return 'None';
        }
        return 'Text';
    }

    return survey.progressIndicatorType;
}

export function getNextButtonColor(survey: SurveyDefinition, step: SurveyStepDefinition) {
    const resolvedStyles = stepStyles(survey, step);
    return resolvedStyles.nextButtonBackgroundColor;
}

export function getNextButtonColorDark(survey: SurveyDefinition, step: SurveyStepDefinition) {
    const resolvedStyles = stepStyles(survey, step);
    return darkFill(resolvedStyles, "nextButtonBackgroundColor");
}

export function getContentItemSettings(survey: SurveyDefinition, step: SurveyStepDefinition) {
    const resolvedStyles = stepStyles(survey, step);

    const iconColor = resolvedStyles.contentItemIconColor || "#FFFFFF";
    const iconBackground = resolvedStyles.contentItemIconBackground || "Circle";
    const iconBackgroundColor = resolvedStyles.contentItemIconBackgroundColor || ORG_COLOR;
    const iconSize = resolvedStyles.contentItemIconSize || "Medium";
    const iconAlignment = resolvedStyles.contentItemIconAlignment || "Top";
    const iconColorDark = darkFill(resolvedStyles, "contentItemIconColor") || iconColor;
    const iconBackgroundColorDarkValue = darkFill(resolvedStyles, "contentItemIconBackgroundColor") || iconBackgroundColor;
    const iconBackgroundColorDark = iconBackgroundColorDarkValue === ORG_COLOR ? undefined : iconBackgroundColorDarkValue;

    return {
        iconColor,
        iconBackground,
        iconBackgroundColor,
        iconSize,
        iconAlignment,
        iconColorDark,
        iconBackgroundColorDark
    };
}

export function getNextButtonStyles(survey: SurveyDefinition, step: SurveyStepDefinition, primary: boolean = false, react: boolean = false, backgroundColorOverride?: string) {
    const resolvedStyles = stepStyles(survey, step);

    var backgroundImage = gradientImage(resolvedStyles.nextButtonBackgroundGradient);
    var backgroundImageDark = darkGradientImage(resolvedStyles) || backgroundImage || "none";

    var letterSpacing = resolvedStyles.nextButtonLetterSpacing;
    var textTransform = resolvedStyles.nextButtonTextTransform;
    var fontWeight = resolvedStyles.nextButtonFontWeight;
    let backgroundColorBase = backgroundColorOverride || resolvedStyles.nextButtonBackgroundColor;
    let backgroundColor = primary ? backgroundColorBase : "var(--survey-control-background-color)";
    let textColor = primary ? resolvedStyles.nextButtonTextColor : backgroundColorBase;

    let backgroundColorBaseDark = backgroundColorOverride || darkFill(resolvedStyles, "nextButtonBackgroundColor");
    let backgroundColorDark = primary ? backgroundColorBaseDark : backgroundColor;
    let textColorDark = primary ? darkFill(resolvedStyles, "nextButtonTextColor") : (backgroundColorOverride || "var(--survey-accent-color)");
    const darkVars = {
        "--next-button-background-dark": backgroundColorDark,
        "--next-button-text-dark": textColorDark,
        "--next-button-background-image-dark": backgroundImageDark
    };

    if (react) {
        return {
            letterSpacing: letterSpacing ? letterSpacing + "px" : null,
            textTransform: textTransform ? textTransform.toLowerCase() as "none" | "capitalize" | "uppercase" | "lowercase" : null,
            color: textColor,
            backgroundColor: backgroundColor,
            backgroundImage: backgroundImage,
            borderColor: backgroundColor,
            fontWeight: fontWeight ? fontWeight.toLowerCase() as "normal" | "bold" | "bolder" | "lighter" : null,
            ...darkVars
        } as React.CSSProperties;
    }

    return {
        "letter-spacing": letterSpacing ? letterSpacing + "px" : null,
        "text-transform": textTransform ? textTransform.toLowerCase() : null,
        "color": textColor,
        "background-color": backgroundColor,
        "background-image": backgroundImage,
        "border-color": backgroundColor,
        "font-weight": fontWeight ? fontWeight.toLowerCase() : null,
        ...darkVars
    };
}

export function getBackgroundStyles(survey: SurveyDefinition, step: SurveyStepDefinition,) {
    const resolvedStyles = stepStyles(survey, step);
    if (!getFormStyle(survey, step)) {
        return {
            "background-color": "var(--survey-card-background-color)"
        };
    }
    if (getFormStyle(survey, step) == "cardBased" && resolvedStyles.backgroundColor) {
        return {
            "background-color": "var(--survey-page-background-color)",
            "--survey-step-bg-dark": "var(--survey-page-background-color)"
        };
    }
    var customBgDark = darkText(resolvedStyles, "backgroundColor");
    return {
        "background-color": resolvedStyles.backgroundColor,
        ...(customBgDark ? { "--survey-step-bg-dark": customBgDark } : {})
    };
}

export function getFormStyle(survey: SurveyDefinition, step: SurveyStepDefinition,) {
    if (survey.useWebSurveyEngine) {
        return "v2";
    }
    return stepStyles(survey, step).formStyle;
}

export function getTextStyles(element: "title" | "text" | "detailText" | "footnote", survey: SurveyDefinition, step: SurveyStepDefinition, react: boolean) {
    const resolvedStyles = stepStyles(survey, step);
    function extractStyles(color?: string, fontSize?: string, fontWeight?: string, textAlign?: string, darkColor?: string) {
        if (!react) {
            return {
                "color": color,
                "font-size": fontSize ? fontSize + "px" : null,
                "font-weight": fontWeight ? fontWeight.toLowerCase() : null,
                "text-align": textAlign ? textAlign.toLowerCase() : null,
                "--custom-text-color-dark": darkColor || null
            }
        } else {
            return {
                color: color,
                fontSize: fontSize ? fontSize + "px" : null,
                fontWeight: fontWeight ? fontWeight.toLowerCase() : null,
                textAlign: textAlign ? textAlign.toLowerCase() as "left" | "right" | "center" | "justify" : null,
                ["--custom-text-color-dark" as any]: darkColor || null
            } as React.CSSProperties;
        }
    }

    return extractStyles(resolvedStyles[`${element}Color`], resolvedStyles[`${element}FontSize`],
        resolvedStyles[`${element}FontWeight`], resolvedStyles[`${element}Alignment`],
        darkText(resolvedStyles, `${element}Color`));
}

export function getIconImageStyles(survey: SurveyDefinition, step: SurveyStepDefinition) {
    const resolvedStyles = stepStyles(survey, step);

    return {
        maxHeight: resolvedStyles.iconImageSize,
        alignment: resolvedStyles.iconImageAlignment
    }
}